import { Grid, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Calendar, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CardSignatureProps } from "./CardSignature.types";
import { LEVEL } from "../../consts";
import { formatDate } from "../../services";
import { formatDuration } from "../../utils/datetime";
import { Icon } from "../Icon";

export function CardSignature({
  color,
  direction = "row",
  duration,
  ecm_specialization,
  editionNumber,
  endDate,
  expirationDate,
  gap = 1,
  learningActivityNumber=0,
  learningObjectTypology,
  meetingDetails,
  minimumScore,
  resources,
  startDate,
  temptative,
  isSurvey,
  isTest,
  isToj,
  canAccessNow,
  ...props
}: CardSignatureProps) {

  const { i18n, t } = useTranslation();
  const theme = useTheme();

  const meetingStartDate = meetingDetails?.[0]?.startDate?.split(" ")?.[0];
  const meetingEndDate = meetingDetails?.[0]?.endDate?.split(" ")?.[0];

  const dateFormatted = () =>  {
    if(meetingStartDate && meetingEndDate){
      if(meetingStartDate !== meetingEndDate){
        return {
          date: meetingStartDate?.replaceAll("-","/"),
          end:meetingEndDate?.replaceAll("-","/"),
          type: "ecm_2"
        };
      }
      else {
        return {
          date:meetingStartDate,
          end:"",
          type: "ecm"
        };
      }
    }
    if(!startDate ){
      if(
        learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
        || learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
      ) {
        return   {
          date:"",
          end:"",
          type:"editions"
        };
      } else if (expirationDate){
        return {
          date: formatDate( expirationDate.join("/"), i18n.language),
          end:"",
          type:"normal"
        };
      }  else {
        return   {
          date:"",
          end:"",
          type:"normal"
        };
      }
    }
    else if (endDate && (startDate.join("/") !== endDate.join("/"))) {
      return {
        date: formatDate(startDate.join("/"), i18n.language),
        end:formatDate(endDate.join("/"), i18n.language),
        type: ecm_specialization || isToj ? "ecm_2" :"register_2"
      };
    }
    else {
      return {
        date: formatDate( startDate.join("/"), i18n.language),
        end:"",
        type: ecm_specialization || isToj ? "ecm" :"register"
      };
    }
  };

  return (
    <Grid
      item
      container
      className={ "card-signature" }
      display={ "flex" }
      flexDirection={ direction }
      gap={ gap }
      height={ "fit-content" }
      { ...props }
    >
      {
        /* duration */
        duration && !isToj && ! meetingDetails ? (
          <Stack
            alignItems="center"
            direction="row"
            gap={ 1 }
            height={ "fit-content" }

          >
            <Icon
              aria-hidden="true"
              icon={ "Icons_time" }
              size={ 24 }
              color={ color || theme.customColors.textPrimary }
            />
            <Typography
              aria-label={ t("duration") + formatDuration(duration, t) }
              sx={ {
                [theme.breakpoints.down("sm")]: {
                  fontSize:"0.875rem",
                  lineHeight: "18px"
                },
                color: color|| theme.customColors.textPrimary
              } }
            >
              { duration && formatDuration(duration, t) }
            </Typography>
          </Stack>
        ):
          null
      }

      {
        /* tempatives */
        temptative ? (
          <Stack
            alignItems="center"
            direction="row"
            gap={ 1 }
            height={ "fit-content" }

          >
            <Icon
              icon={ "test" }
              size={ 24 }
              color={ color || theme.customColors.textPrimary }
            />
            <Typography
              sx={ {
                [theme.breakpoints.down("sm")]: {
                  fontSize:"0.875rem",
                  lineHeight: "18px"
                },
                color: color || theme.customColors.textPrimary
              } }
            >
              { t("temptatives", { count: temptative }) }
            </Typography>
          </Stack>
        ):
          null
      }
      {
        /* minimunScore */
        minimumScore && !isSurvey && isTest ? (
          <Stack
            alignItems="center"
            direction="row"
            gap={ 1 }
            height={ "fit-content" }

          >
            <Icon
              icon={ "completato" }
              size={ 24 }
              color={ color || theme.customColors.textPrimary }
            />
            <Typography
              sx={ {
                [theme.breakpoints.down("sm")]: {
                  fontSize:"0.875rem",
                  lineHeight: "18px"
                },
                color: color || theme.customColors.textPrimary
              } }
            >
              { t("minimum_score", { count: minimumScore }) }
            </Typography>
          </Stack>
        ):
          null
      }

      {
        /* activities */
        (learningActivityNumber > 0) ? (
          <Stack
            alignItems="center"
            direction="row"
            gap={ 1 }
            height={ "fit-content" }
          >
            <Icon
              icon={ "attivita" }
              size={ 24 }
              color={ color || theme.customColors.textPrimary }
            />
            <Typography
              sx={ {
                [theme.breakpoints.down("sm")]: {
                  fontSize:"0.875rem"
                },
                color: color || theme.customColors.textPrimary
              } }
            >
              { t(learningActivityNumber === 1 ? "activity_one" : "activity_other", { count: learningActivityNumber }) }
            </Typography>
          </Stack>
        ) : null
      }
      {
        /* resources */
        resources ? (
          <Stack
            alignItems="center"
            direction="row"
            gap={ 1 }
            height={ "fit-content" }
          >
            <Icon
              icon={ "Icons_risorse" }
              color={ color || theme.customColors.textPrimary }
              size={ 24 }
            />
            <Typography
              sx={ {
                [theme.breakpoints.down("sm")]: {
                  fontSize:"0.875rem"
                },
                color: color || theme.customColors.textPrimary
              } }
            >
              { t("resource", { count: resources }) }
            </Typography>
          </Stack>
        ) : null
      }
      { /* editions */
        (
          dateFormatted().type === "editions" &&
          editionNumber &&
          editionNumber > 0
        )  ?
          <Stack
            alignItems="center"
            direction="row"
            gap={ 1 }
            height={ "fit-content" }
          >
            <Calendar 
              size={ "1rem" }
              color={ color || theme.customColors.exploreCardText }
            />
            <Typography
              fontSize="0.875rem"
              color={ color || theme.customColors.exploreCardText }
            
            >
              { t("editions", { count: editionNumber }) }
            </Typography>
          </Stack> : null
      }
      {
        meetingDetails?.[0]?.meetingNumber ?
          <Stack
            alignItems="center"
            direction="row"
            gap={ 1 }
            height={ "fit-content" }
          >
            <Users 
              size={ "1rem" }
              color={ color || theme.customColors.exploreCardText }
            />
            <Typography
              color={ color || theme.customColors.exploreCardText }
              fontSize="0.875rem"
            >
              { t("meetings", { count: meetingDetails?.[0]?.meetingNumber }) }
            </Typography>
          </Stack> : null
      }
    </Grid>
  );
}
